<template>
  <TitleWrapper
    :filter-option="false"
    title="NOTICE_BOARD_VIEW"
    tooltip-title="NOTICE_BOARD_VIEW"
    :display-breadcrumb="true"
  />
  <div class="p-4 md:p-6 h-full overflow-y-scroll">
    <template v-if="isLoading"><Loader class="mt-20 mb-40" :content="true" /></template>
    <NoRecordFound v-else-if="!isLoading && isEmpty(posts)" />
    <div
      v-for="post in posts"
      v-else
      :key="post.id"
      class="bg-bg-color-white border border-border rounded-lg p-4 md:p-5 my-5 mx-auto lg:w-[70%] md:w-[90%] w-full shadow-lg hover:shadow-xl transition-shadow duration-200"
    >
      <div class="relative flex flex-col gap-2">
        <div class="flex items-start md:items-center justify-between">
          <div class="flex flex-col md:flex-row md:items-center md:gap-3">
            <AuthorCard
              :author="{ name: getAuthorName(post) }"
              :time="getPostTime(post)"
              :labels="post?.labels || []"
            />
            <div v-if="post?.labels?.length" class="flex flex-wrap gap-2 mt-1 md:mt-0">
              <Chips
                v-for="label in post?.labels"
                :key="label.id"
                :title="label.title"
                child-class="bg-primary-purple-50 text-primary-purple-700 border-primary-purple-200"
              />
            </div>
          </div>
          <PostActions
            :action-list="actionList"
            :current-user="post"
            :idx="post.id"
            :top="-12"
            :right="50"
            style="width: 54px"
            elipses="verticalElipses"
            @action="postActionsHandler"
          />
        </div>
      </div>

      <div class="overflow-hidden">
        <PostMarkdown :text="getPostText(post)" />
      </div>

      <PostMedia :media="post.media" />

      <div class="flex items-center gap-3 mt-4 text-primary-gray-700">
        <UIButton
          :size="isTabView ? 'xs' : 'base'"
          :button="isPostLiked(post) ? 'primary' : 'primary-button-outline'"
          @click="toggleLike(post)"
        >
          <span
            class="inline-flex items-center gap-2"
            :class="isPostLiked(post) ? 'text-white font-semibold' : 'text-primary-gray-700'"
          >
            <icon class="w-5 h-5" :color="isPostLiked(post) ? 'white' : 'primary-gray-700'" />
            <span class="text-sm">
              {{ getPostLikes(post) }} Like{{ getPostLikes(post) === 1 ? '' : 's' }}
            </span>
          </span>
        </UIButton>
        <UIButton
          :size="isTabView ? 'xs' : 'base'"
          button="primary-button-outline"
          @click="openComments(post)"
        >
          <span class="inline-flex items-center gap-2">
            <span class="text-sm">Comments ({{ getPostCommentsCount(post) }})</span>
          </span>
        </UIButton>
      </div>
    </div>

    <CommentModal
      v-if="showComments"
      :modal="showComments"
      :post="activePost"
      @toggle="handleModalAction"
      @submitComment="submitComment"
      @toggleCommentLike="toggleCommentLike"
      @submitReply="submitReply"
      @toggleReplyLike="toggleReplyLike"
      @togglePostLike="togglePostLikeInModal"
    />

    <CreateEditNoticeBoardPost
      v-if="showCreatePost"
      :modal="showCreatePost"
      :edit="currentPost"
      @toggle="toggleCreateOrEditPost"
    />

    <UIConfirmationModal
      v-if="showDeleteConfirm"
      :modal="showDeleteConfirm"
      heading="DELETE_POST"
      message="Are you sure you want to delete "
      :name="getAuthorName(currentPost)"
      remaining-message="Post ?"
      button-text="Delete"
      :is-loading="isDeleting"
      @cancel="toggleDeleteConfirm"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import icon from '@components/icons/icon.vue'
import AuthorCard from '@src/components/notice-board/AuthorCard.vue'
import PostMarkdown from '@src/components/notice-board/PostMarkdown.vue'
import PostMedia from '@src/components/notice-board/PostMedia.vue'
import TitleWrapper from '@components/TitleWrapper.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import CommentModal from '@src/components/notice-board/modals/CommentModal.vue'
import CreateEditNoticeBoardPost from '@src/components/notice-board/modals/CreateEditNoticeBoardPost.vue'
import PostActions from '@components/TableAction.vue'
import UIConfirmationModal from '@components/UiElements/UIConfirmationModal.vue'
import { mapActions, mapState } from 'vuex'
import GENERAL_CONSTANTS from '@src/constants/general-constants'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { isEmpty } from 'lodash'
import formatDateRelative from '@src/utils/format-date-relative'
import dummyPostsData from './dummy-posts.json'
import Chips from '@src/components/UiElements/Chips.vue'

export default {
  name: 'NoticeBoardView',
  components: {
    icon,
    AuthorCard,
    PostMarkdown,
    PostMedia,
    TitleWrapper,
    UIButton,
    CommentModal,
    CreateEditNoticeBoardPost,
    NoRecordFound,
    PostActions,
    Loader,
    UIConfirmationModal,
    Chips,
  },
  data() {
    return {
      posts: [],
      isLoading: false,
      actionList: [{ name: 'Edit' }, { name: 'Delete' }],
      // Keep dummy posts for fallback
      dummyPosts: dummyPostsData,
      showComments: false,
      activePost: null,
      showCreatePost: false,
      currentPost: null,
      showDeleteConfirm: false,
      isDeleting: false,
    }
  },
  computed: {
    ...mapState({
      isTabView: (state) => state.layout.isTabView,
    }),
  },
  mounted() {
    this.setRightbarData()
    this.filterRecords()
  },
  methods: {
    isEmpty,
    isPostLiked(post) {
      return Boolean(post?.liked_by_you)
    },
    filterRecords() {
      this.isLoading = true
      this.getPosts()
        .then((response) => {
          this.posts = response.data.records || []
          const postsCount = response.data.meta.total_records || 0
          this.setRightbarData(postsCount)
        })
        .catch((error) => {
          console.error('Failed to load posts:', error)
          this.posts = this.dummyPosts
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    postActionsHandler(action, current) {
      this.currentPost = current
      switch (action) {
        case 'Edit':
          this.toggleCreateOrEditPost()
          break
        case 'Delete':
          this.toggleDeleteConfirm()
          break
      }
    },
    rendered(text) {
      return renderMarkdown(text)
    },
    getAuthorName(post) {
      return post?.inserted_by?.full_name || ''
    },
    getPostTime(post) {
      const timestamp = post?.inserted_at || post?.time
      if (timestamp) return formatDateRelative(new Date(timestamp))
      return ''
    },
    getPostText(post) {
      return post?.description || post?.text || ''
    },
    getPostLikes(post) {
      return post?.likes_count || 0
    },
    getPostCommentsCount(post) {
      return post?.comments_count || 0
    },
    toggleLike(post) {
      this.likePostOrCommentOrReply({
        post_id: post.id,
        emoji: 'like',
      })
        .then((res) => {
          const isUnliked = res?.data?.message?.toLowerCase().includes('unliked')
          post.liked_by_you = !isUnliked
          post.likes_count = isUnliked
            ? Math.max(0, (post.likes_count || 0) - 1)
            : (post.likes_count || 0) + 1
        })
        .catch(console.error)
    },
    openComments(post) {
      this.activePost = post
      this.showComments = true
    },
    handleModalAction(action) {
      if (action === 'close' || action === 'cancel') {
        this.showComments = false
      }
    },
    submitComment() {
      if (!this.activePost) return
      this.activePost.comments_count += 1
    },
    toggleCommentLike(index) {
      if (!this.activePost) return
      const comment = this.activePost.comments[index]
      if (!comment || !comment.id) return

      this.likePostOrCommentOrReply({
        comment_id: comment.id,
        emoji: 'like',
      })
        .then((res) => {
          const isUnliked = res?.data?.message?.toLowerCase().includes('unliked')

          comment.liked = !isUnliked
          comment.likes = isUnliked
            ? Math.max(0, (comment.likes || 0) - 1)
            : (comment.likes || 0) + 1
        })
        .catch(console.error)
    },
    submitReply(commentIndex, replyText) {
      if (!this.activePost || !replyText.trim()) return
      const comment = this.activePost.comments[commentIndex]
      if (!comment) return

      if (!comment.replies) {
        comment.replies = []
      }

      comment.replies.push({
        author: 'You',
        time: 'now',
        text: replyText,
        liked: false,
        likes: 0,
      })
    },
    toggleReplyLike(commentIndex, replyIndex) {
      if (!this.activePost) return
      const comment = this.activePost.comments[commentIndex]
      if (!comment || !comment.replies) return
      const reply = comment.replies[replyIndex]
      if (!reply || !reply.id) return

      this.likePostOrCommentOrReply({
        reply_id: reply.id,
        emoji: 'like',
      })
        .then((res) => {
          const isUnliked = res?.data?.message?.toLowerCase().includes('unliked')

          reply.liked = !isUnliked
          reply.likes = isUnliked ? Math.max(0, (reply.likes || 0) - 1) : (reply.likes || 0) + 1
        })
        .catch(console.error)
    },
    togglePostLikeInModal() {
      if (!this.activePost) return
      this.toggleLike(this.activePost)
    },
    toggleCreateOrEditPost(action) {
      this.showCreatePost = !this.showCreatePost
      if (!this.showCreatePost) this.currentPost = null
      if (action) this.filterRecords()
    },
    toggleDeleteConfirm() {
      this.showDeleteConfirm = !this.showDeleteConfirm
      if (!this.showDeleteConfirm) this.currentPost = null
    },
    confirmDelete() {
      this.isDeleting = true
      this.deactivatePost(this.currentPost.id)
        .then(() => {
          this.toggleDeleteConfirm()
          this.filterRecords()
        })
        .finally(() => {
          this.isDeleting = false
          this.currentPost = null
        })
    },
    setRightbarData(statsCount = 0) {
      const rightBarContent = {
        header: {
          title: 'Post',
          buttons: [
            {
              title: 'CREATE_NEW_POST',
              classes: ['primary-button-right'],
              action: {
                func: () => {
                  this.toggleCreateOrEditPost()
                },
              },
            },
          ],
        },
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [
              {
                text: 'TOTAL_POST_COUNTS',
                value: statsCount,
                tooltipDescription: 'Total types of posts',
              },
            ],
          },
        ],
      }
      this.setRightbarContent(rightBarContent)
    },

    ...mapActions('noticeboard', ['getPosts', 'deactivatePost', 'likePostOrCommentOrReply']),
    ...mapActions('layout', ['setRightbarContent', 'setShowModal']),
  },
}
</script>
